import { useState, useEffect, useCallback } from 'react';
import {
    X, Search, Loader2, FolderOpen, Image, Film, FileText, ChevronRight, ChevronDown, Check,
} from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';

const csrf = () => document.querySelector('meta[name="csrf-token"]')?.content;
const api = async (url) => {
    const r = await fetch(url, { headers: { Accept: 'application/json', 'X-CSRF-TOKEN': csrf() } });
    if (!r.ok) throw new Error();
    return r.json();
};

const fileIcon = (mime) => {
    if (!mime) return FileText;
    if (mime.startsWith('image/')) return Image;
    if (mime.startsWith('video/')) return Film;
    return FileText;
};

export default function MediaPickerModal({ open, onClose, onSelect, accept }) {
    const { theme } = useTheme();
    const isDark = theme === 'dark' || (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);

    const ACCENT = '#0CC0DF';
    const borderColor = isDark ? 'rgba(255,255,255,0.10)' : 'var(--border-default)';
    const mutedColor = isDark ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)';
    const secondaryColor = isDark ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.55)';
    const cardBg = isDark ? '#1a1a2e' : '#fff';
    const inputStyle = {
        width: '100%', padding: '8px 12px', borderRadius: 6, fontSize: 13,
        border: `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : 'var(--border-default)'}`,
        background: isDark ? 'rgba(255,255,255,0.06)' : '#fff',
        color: 'var(--text-primary)', outline: 'none',
    };

    const [slozky, setSlozky] = useState([]);
    const [soubory, setSoubory] = useState([]);
    const [activePath, setActivePath] = useState(null); // null = vše
    const [expandedFolders, setExpandedFolders] = useState({});
    const [search, setSearch] = useState('');
    const [loading, setLoading] = useState(false);
    const [loadingFolders, setLoadingFolders] = useState(false);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(false);
    const [selected, setSelected] = useState(null);

    // Load folders
    const loadFolders = useCallback(async () => {
        setLoadingFolders(true);
        try {
            const res = await api('/api/media/slozky');
            setSlozky(Array.isArray(res) ? res : res.data || []);
        } catch {}
        setLoadingFolders(false);
    }, []);

    // Load files
    const loadFiles = useCallback(async (path, searchQ, pageNum = 1) => {
        setLoading(true);
        try {
            let url = `/api/media?per_page=60&page=${pageNum}`;
            if (path) url += `&slozka=${encodeURIComponent(path)}`;
            if (searchQ?.trim()) url += `&hledat=${encodeURIComponent(searchQ.trim())}`;
            if (accept) url += `&typ=${encodeURIComponent(accept)}`;
            const res = await api(url);
            const items = Array.isArray(res.data) ? res.data : Array.isArray(res) ? res : [];
            if (pageNum === 1) setSoubory(items);
            else setSoubory(prev => [...prev, ...items]);
            setHasMore(res.last_page ? pageNum < res.last_page : false);
        } catch { if (pageNum === 1) setSoubory([]); }
        setLoading(false);
    }, [accept]);

    useEffect(() => {
        if (!open) return;
        loadFolders();
        setActivePath(null);
        setSearch('');
        setSelected(null);
        setPage(1);
        loadFiles(null, '', 1);
    }, [open]);

    useEffect(() => {
        if (!open) return;
        setPage(1);
        loadFiles(activePath, search, 1);
    }, [activePath]);

    useEffect(() => {
        if (!open) return;
        const t = setTimeout(() => { setPage(1); loadFiles(activePath, search, 1); }, 300);
        return () => clearTimeout(t);
    }, [search]);

    const loadMore = () => {
        const next = page + 1;
        setPage(next);
        loadFiles(activePath, search, next);
    };

    const toggleFolder = (id) => setExpandedFolders(prev => ({ ...prev, [id]: !prev[id] }));

    const handleSelect = () => {
        if (selected) { onSelect(selected.id, selected); onClose(); }
    };

    // Build folder tree
    const buildTree = (items, parentId = null) => {
        return items
            .filter(f => (f.rodic_id || null) === parentId)
            .sort((a, b) => (a.poradi || 0) - (b.poradi || 0))
            .map(f => ({ ...f, children: buildTree(items, f.id) }));
    };
    const folderTree = buildTree(slozky);

    const renderFolder = (folder, depth = 0) => {
        const hasChildren = folder.children && folder.children.length > 0;
        const isExpanded = expandedFolders[folder.id];
        const isActive = activePath === folder.cesta;
        return (
            <div key={folder.id}>
                <div className="flex items-center gap-1 px-2 py-1.5 rounded cursor-pointer text-xs"
                    style={{
                        paddingLeft: 8 + depth * 16,
                        background: isActive ? `${ACCENT}15` : 'transparent',
                        color: isActive ? ACCENT : 'var(--text-primary)',
                        fontWeight: isActive ? 600 : 400,
                    }}
                    onClick={() => {
                        setActivePath(isActive ? null : folder.cesta);
                        if (hasChildren) toggleFolder(folder.id);
                    }}>
                    {hasChildren ? (
                        isExpanded ? <ChevronDown size={12} style={{ flexShrink: 0 }} /> : <ChevronRight size={12} style={{ flexShrink: 0 }} />
                    ) : <div style={{ width: 12, flexShrink: 0 }} />}
                    <span style={{ marginRight: 4 }}>{folder.ikona || '📁'}</span>
                    <span className="truncate flex-1">{folder.nazev}</span>
                </div>
                {hasChildren && isExpanded && folder.children.map(c => renderFolder(c, depth + 1))}
            </div>
        );
    };

    if (!open) return null;

    return (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            onClick={onClose}>
            <div className="rounded-2xl overflow-hidden flex flex-col" style={{ width: '90vw', maxWidth: 960, height: '80vh', background: cardBg, border: `1px solid ${borderColor}` }}
                onClick={e => e.stopPropagation()}>

                {/* Header */}
                <div className="flex items-center justify-between px-5 py-3" style={{ borderBottom: `1px solid ${borderColor}` }}>
                    <h2 className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>Mediální knihovna</h2>
                    <div className="flex items-center gap-2">
                        {selected && (
                            <button onClick={handleSelect}
                                className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold"
                                style={{ background: ACCENT, color: '#fff', border: 'none', cursor: 'pointer' }}>
                                <Check size={13} /> Vybrat
                            </button>
                        )}
                        <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: secondaryColor }}>
                            <X size={18} />
                        </button>
                    </div>
                </div>

                <div className="flex flex-1 min-h-0">
                    {/* Left — folder tree */}
                    <div className="flex flex-col py-2" style={{ width: 220, minWidth: 180, borderRight: `1px solid ${borderColor}`, flexShrink: 0 }}>
                        {/* All files button */}
                        <div className="px-2 mb-1">
                            <div className="flex items-center gap-1.5 px-2 py-1.5 rounded cursor-pointer text-xs"
                                style={{
                                    background: activePath === null ? `${ACCENT}15` : 'transparent',
                                    color: activePath === null ? ACCENT : 'var(--text-primary)',
                                    fontWeight: activePath === null ? 600 : 400,
                                }}
                                onClick={() => setActivePath(null)}>
                                <FolderOpen size={13} />
                                <span>Vše</span>
                            </div>
                        </div>
                        <div className="flex-1 overflow-y-auto px-1">
                            {loadingFolders ? (
                                <div className="flex justify-center py-6"><Loader2 size={16} className="animate-spin" style={{ color: mutedColor }} /></div>
                            ) : (
                                folderTree.map(f => renderFolder(f))
                            )}
                        </div>
                    </div>

                    {/* Right — file grid */}
                    <div className="flex-1 flex flex-col min-w-0">
                        {/* Search */}
                        <div className="px-4 py-3" style={{ borderBottom: `1px solid ${borderColor}` }}>
                            <div className="relative">
                                <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: mutedColor, pointerEvents: 'none' }} />
                                <input value={search} onChange={e => setSearch(e.target.value)}
                                    placeholder="Hledat soubor..."
                                    style={{ ...inputStyle, paddingLeft: 32 }} />
                            </div>
                        </div>

                        {/* Grid */}
                        <div className="flex-1 overflow-y-auto p-4">
                            {loading && soubory.length === 0 ? (
                                <div className="flex justify-center items-center h-full">
                                    <Loader2 size={24} className="animate-spin" style={{ color: mutedColor }} />
                                </div>
                            ) : soubory.length === 0 ? (
                                <div className="flex flex-col items-center justify-center h-full" style={{ color: mutedColor }}>
                                    <Image size={36} style={{ opacity: 0.3, marginBottom: 8 }} />
                                    <p className="text-sm">Žádné soubory</p>
                                </div>
                            ) : (
                                <>
                                    <div className="grid gap-2" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))' }}>
                                        {soubory.map(f => {
                                            const isSelected = selected?.id === f.id;
                                            const thumb = f.thumbnail_cesta || (f.mime_type?.startsWith('image/') ? f.cesta : null);
                                            const FIcon = fileIcon(f.mime_type);
                                            return (
                                                <div key={f.id}
                                                    className="rounded-lg overflow-hidden cursor-pointer relative group"
                                                    onClick={() => setSelected(isSelected ? null : f)}
                                                    onDoubleClick={() => { onSelect(f.id, f); onClose(); }}
                                                    style={{
                                                        aspectRatio: '1',
                                                        background: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)',
                                                        border: isSelected ? `2px solid ${ACCENT}` : `1px solid ${borderColor}`,
                                                        transition: 'border-color 0.1s',
                                                    }}>
                                                    {thumb ? (
                                                        <img src={`/storage/${thumb}`} alt={f.nazev} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                                    ) : (
                                                        <div className="flex items-center justify-center h-full">
                                                            <FIcon size={28} style={{ color: mutedColor, opacity: 0.4 }} />
                                                        </div>
                                                    )}
                                                    {isSelected && (
                                                        <div className="absolute top-1.5 right-1.5 w-5 h-5 rounded-full flex items-center justify-center"
                                                            style={{ background: ACCENT }}>
                                                            <Check size={12} style={{ color: '#fff' }} />
                                                        </div>
                                                    )}
                                                    <div className="absolute bottom-0 left-0 right-0 px-1.5 py-1 text-[10px] truncate"
                                                        style={{ background: isDark ? 'rgba(0,0,0,0.7)' : 'rgba(255,255,255,0.85)', color: 'var(--text-primary)' }}>
                                                        {f.nazev}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                    {hasMore && (
                                        <div className="flex justify-center mt-4">
                                            <button onClick={loadMore} disabled={loading}
                                                className="flex items-center gap-1 px-4 py-2 rounded-lg text-xs font-medium"
                                                style={{ background: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)', border: `1px solid ${borderColor}`, color: secondaryColor, cursor: 'pointer' }}>
                                                {loading ? <Loader2 size={12} className="animate-spin" /> : null}
                                                Načíst další
                                            </button>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>

                        {/* Footer — selected info */}
                        {selected && (
                            <div className="flex items-center gap-3 px-4 py-2" style={{ borderTop: `1px solid ${borderColor}`, background: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)' }}>
                                <div className="w-8 h-8 rounded overflow-hidden flex-shrink-0" style={{ background: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)' }}>
                                    {(selected.thumbnail_cesta || selected.cesta) && selected.mime_type?.startsWith('image/')
                                        ? <img src={`/storage/${selected.thumbnail_cesta || selected.cesta}`} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                        : <div className="flex items-center justify-center h-full"><FileText size={14} style={{ color: mutedColor }} /></div>}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className="text-xs font-medium truncate" style={{ color: 'var(--text-primary)' }}>{selected.nazev}</p>
                                    <p className="text-[10px]" style={{ color: secondaryColor }}>
                                        {selected.mime_type} {selected.velikost ? `• ${(selected.velikost / 1024).toFixed(0)} KB` : ''}
                                        {selected.sirka ? ` • ${selected.sirka}×${selected.vyska}` : ''}
                                    </p>
                                </div>
                                <button onClick={handleSelect}
                                    className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold"
                                    style={{ background: ACCENT, color: '#fff', border: 'none', cursor: 'pointer' }}>
                                    <Check size={13} /> Vybrat
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
